import { ProductListProductWithAccountRequestDto } from '@epicuro-next/data/engagement/engagement-models';
import {
  filterOutUnavailableDeliveryInfo,
  isCommercialList,
} from '@epicuro-next/features/engagement/list-utils';
import { ProductReference } from '@epicuro-next/models/engagement';
import { ListProductReferenceEntity } from '@epicuro-next/state/list-product-reference-entities';
import { isEqual, isNonNullish, isNullish } from '@epicuro-next/utilities';
import { SearchSortType } from 'graphql-api/gql-api-interfaces';

import {
  loadNextListProducts,
  QUERY_UPDATE_STRATEGY,
} from './list-entities.actions';
import { ListEntity, ListProductsQuery } from './model';

export const LIST_PRODUCT_QUERY_STEP = 10;

export function getDefaultListProductsQuery(listId: string): ListProductsQuery {
  return {
    productListId: listId,
    start: 0,
    num: LIST_PRODUCT_QUERY_STEP,
    available: true,
  };
}

export function allProductsLoaded(listEntity: ListEntity) {
  const { count, query, list, loadedProductIds } = listEntity;
  const start = query?.start;

  if (list && isCommercialList(list)) {
    return isNonNullish(count) && (loadedProductIds?.length ?? 0) >= count;
  }

  if (isNullish(count) || isNullish(start)) return false;
  return start >= count;
}

export function newQueryForNextProductsAction(
  action: ReturnType<typeof loadNextListProducts>,
  entity?: ListEntity,
): ListProductsQuery {
  const { query, listId, strategy } = action;
  const oldQuery = entity?.query;
  const defaultQuery = getDefaultListProductsQuery(listId);

  if (strategy === QUERY_UPDATE_STRATEGY.MERGE) {
    const mergedQuery = {
      ...defaultQuery,
      ...oldQuery,
      ...query,
    };
    mergedQuery.start = areQueriesSame(mergedQuery, oldQuery)
      ? mergedQuery.start ?? 0
      : 0;
    return mergedQuery;
  }

  const overrideQuery = !!query
    ? { ...defaultQuery, ...query }
    : oldQuery ?? defaultQuery;
  return { ...overrideQuery, start: 0 };
}

/**
 * A method to check if 2 given queries are same except the pagination
 * @param a First query
 * @param b Second query
 */
export function areQueriesSame(a?: ListProductsQuery, b?: ListProductsQuery) {
  return isEqual({ ...a, start: undefined }, { ...b, start: undefined });
}

export function getNotLoadedCommercialListProductReferences(
  listEntity: ListEntity,
  referenceEntity: ListProductReferenceEntity,
) {
  const loadedProductIds = listEntity.loadedProductIds ?? [];
  const availableProducts = filterOutUnavailableDeliveryInfo(
    referenceEntity.data?.productReferences ?? [],
  ).filter((p) => p.deliveryInfo.length > 0);

  const idsToLoad = availableProducts
    .filter((p) => loadedProductIds.indexOf(p.productId) === -1)
    .map((p) => ({
      brandId: p.brandId,
      productId: p.productId,
    }));

  return {
    idsToLoad,
    total: availableProducts.length,
  };
}

export function getProductsQuery(
  query: ListProductsQuery,
  accountId: string,
  siteId: string,
  productReferences?: ProductReference[] | null,
  sortType?: SearchSortType | null,
) {
  const { start, num, available, productListId } = query;
  const isAvailable = isNullish(available) ? true : available;

  const payload: ProductListProductWithAccountRequestDto = {
    start: start ?? 0,
    num: num ?? 0,
    productListId,
    available: isAvailable,
    accountId,
    siteId,
    productReferences,
  };

  if (sortType) payload.sortField = sortType;

  return payload;
}
